import { Platform } from 'react-native';
import i18n from 'i18next';
import { getDeviceLanguage } from '@utils/deviceLanguage';

/**
 * Maps app language keys (from i18n / getDeviceLanguage) to ISO 3166-1 alpha-2
 * country codes for phone inputs (react-phone-input-2, react-native-phone-input).
 */
const APP_LANG_TO_PHONE_COUNTRY: Record<string, string> = {
  en: 'gb',
  'en-GB': 'gb',
  fr: 'fr',
  de: 'de',
  es: 'es',
  el: 'gr',
  ja: 'jp',
  tk: 'tr',
  ar: 'sa',
  zh: 'cn',
  it: 'it',
  si: 'lk',
  ko: 'kr',
  hi: 'in',
  ml: 'in',
  xh: 'za',
  zu: 'za',
  ur: 'pk',
  fil: 'ph',
  ms: 'my',
};

function normalizeIso2(code: string | undefined | null): string | null {
  if (!code || typeof code !== 'string') return null;
  const lower = code.trim().toLowerCase();
  return /^[a-z]{2}$/.test(lower) ? lower : null;
}

/**
 * Preferred region from OS / browser locale (e.g. en-US → us, fr-FR → fr).
 */
function getRegionFromDeviceLocale(): string | null {
  try {
    if (Platform.OS !== 'web') {
      const { getLocales } = require('expo-localization');
      const locales = getLocales?.();
      const region = locales?.[0]?.regionCode;
      return normalizeIso2(region);
    }
    if (typeof navigator !== 'undefined') {
      const tag = navigator.language || (navigator as any).userLanguage || '';
      const m = String(tag).match(/[-_]([A-Za-z]{2})/);
      if (m) return normalizeIso2(m[1]);
    }
  } catch {
    // ignore
  }
  return null;
}

function mapLanguageToCountry(tag: string): string | null {
  if (!tag) return null;
  const normalized = tag.replace('_', '-');
  if (APP_LANG_TO_PHONE_COUNTRY[normalized]) return APP_LANG_TO_PHONE_COUNTRY[normalized];
  const base = normalized.split('-')[0]?.toLowerCase();
  if (base && APP_LANG_TO_PHONE_COUNTRY[base]) return APP_LANG_TO_PHONE_COUNTRY[base];
  const device = getDeviceLanguage();
  if (APP_LANG_TO_PHONE_COUNTRY[device]) return APP_LANG_TO_PHONE_COUNTRY[device];
  return null;
}

/**
 * Default ISO 3166-1 alpha-2 country code for phone fields when the schema does not set one.
 * Uses device region when available, otherwise maps from the active i18n / app language.
 */
export function getDefaultPhoneCountryCode(): string {
  const fromRegion = getRegionFromDeviceLocale();
  if (fromRegion) return fromRegion;

  const fromI18n = mapLanguageToCountry(i18n.language || '');
  if (fromI18n) return fromI18n;

  return 'gb';
}
